import { Button, Card, Col, Input, Pagination, Row, Spin, Table, App, Tooltip, Modal } from 'antd'
import React, { useCallback, useEffect, useState } from 'react'
import { EyeFilled, LoadingOutlined, CopyOutlined, DeleteOutlined, EditFilled, PlusOutlined } from "@ant-design/icons";
import { useNavigate } from 'react-router-dom'
import { LIST_EMAIL, DELETE_EMAIL, COPY_EMAIL, VIEW_EMAIL } from '../../apis/apis';
import debounce from 'lodash.debounce';
import CulsightPageLoader from '../../components/CulsightPageLoader';
import DOMPurify from "dompurify";
import EmailTemplateCard from './EmailTemplateCard';

function EmailList() {
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const [loader, setLoader] = useState(true);
  const [pagination_loader, set_pagination_loader] = useState(false);
  const [table_data, set_table_data] = useState(false);
  const [current_page, set_current_page] = useState("");
  const [total_email_template, set_total_email_template] = useState("");
  const [search_query_name, set_search_query_name] = useState("");

  const [previewModal, setPreviewModal] = useState(false);
  const [previewLoader, setPreviewLoader] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewName, setPreviewName] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");

  // LIST API
  const LIST_API = async () => {
    const FORM_DATA = new FormData();
    const API_CALL = await LIST_EMAIL(FORM_DATA);
    if (API_CALL?.data?.status) {
      set_table_data(API_CALL?.data?.data);
      set_current_page(API_CALL?.data?.current_page);
      set_total_email_template(API_CALL?.data?.total_email_template);

      if (API_CALL?.data?.admin_id) {
        localStorage.setItem("admin_id", API_CALL?.data?.admin_id);
      }
    }
    setLoader(false);
  };

  useEffect(() => {
    LIST_API();
  }, []);

  // PREVIEW FUNCTION
  const handlePreview = async (id) => {
    setPreviewLoader(true);
    setPreviewModal(true);

    const FORM_DATA = new FormData();
    FORM_DATA.append("id", id);
    const API = await VIEW_EMAIL(FORM_DATA);

    if (API?.data?.status) {
      const data = API?.data?.data;
      setPreviewName(data?.name);
      setPreviewSubject(data?.subject);
      setPreviewHtml(data?.message_value || "<h3>No Preview Available</h3>");
    }
    setPreviewLoader(false);
  };

  // COPY EMAIL
  // COPY EMAIL with Confirm
  const handleCopy = (id) => {
    modal.confirm({
      title: "Do you want to duplicate this email template?",
      okText: "Copy",
      cancelText: "Cancel",
      onOk: async () => {
        const FORM_DATA = new FormData();
        FORM_DATA.append("id", id);

        const response = await COPY_EMAIL(FORM_DATA);
        if (response?.data?.status) {
          message.success("Email template copied successfully");
          navigate("/edit-email/" + btoa(response?.data?.data?.id));
        } else {
          message.error("Failed to copy");
        }
      }
    });
  };


  // SEARCH debounce
  const fetchResultsName = useCallback(debounce(async (value) => {
    set_search_query_name(value);
    set_pagination_loader(true);

    const FORM_DATA = new FormData();
    FORM_DATA.append("name", value);
    const API_CALL = await LIST_EMAIL(FORM_DATA);

    if (API_CALL?.data?.status) {
      set_table_data(API_CALL.data?.data);
      set_current_page(API_CALL?.data?.current_page);
      set_total_email_template(API_CALL?.data?.total_email_template);
    }
    set_pagination_loader(false);
  }, 500), []);

  const handleInput = (e) => {
    fetchResultsName(e.target.value);
  };

  const pagination_on_change = async (page) => {
    set_pagination_loader(true);

    const FORM_DATA = new FormData();
    FORM_DATA.append("page", page);
    FORM_DATA.append("name", search_query_name);

    const API_CALL = await LIST_EMAIL(FORM_DATA);

    if (API_CALL?.data?.status) {
      set_table_data(API_CALL.data?.data);
      set_current_page(API_CALL?.data?.current_page);
      set_total_email_template(API_CALL?.data?.total_email_template);
    }
    set_pagination_loader(false);
  };

  // DELETE CONFIRM
  const handleDelete = (id) => {
    modal.confirm({
      title: "Are you sure want to delete?",
      onOk: async () => {
        const FORM_DATA = new FormData();
        FORM_DATA.append("id", id);

        const response = await DELETE_EMAIL(FORM_DATA);
        if (response?.data?.status) {
          message.success("Deleted Successfully");
          LIST_API();
        } else {
          message.error("Failed to delete");
        }
      }
    });
  };

  const columns = [
    { title: "Name", dataIndex: "name" },
    { title: "Subject", dataIndex: "subject" },
    {
      title: "User Count",
      render: (_, record) => <span>{record.email_template_count}</span>,
    },
    {
      title: "Action",
      render: (_, record) => (
        <div style={{ display: "flex", gap: "8px" }}>

          <Tooltip title="Edit">
            <Button icon={<EditFilled />} size="small" type="primary"
              onClick={() => navigate("/edit-email/" + btoa(record.id))}
            />
          </Tooltip>


          <Tooltip title="Copy">
            <Button
              icon={<CopyOutlined />}
              size="small"
              type="primary"
              onClick={() => handleCopy(record.id)}
            />
          </Tooltip>


          <Tooltip title="Preview">
            <Button icon={<EyeFilled />} size="small" type="primary"
              onClick={() => handlePreview(record.id)}
            />
          </Tooltip>

          <Tooltip title="Delete">
            <Button icon={<DeleteOutlined />} danger size="small" type="primary"
              onClick={() => handleDelete(record.id)}
            />
          </Tooltip>

        </div>
      ),
    }
  ];

  return (
    <div className='lms-body'>
      <Row>
        <Col span={12}>
          <h1 style={{ margin: "0px", padding: "0px" }}>Email Templates</h1>
          <span style={{ color: "#6b7280", fontSize: "15px", fontWeight: "600" }}>Create and manage phishing email templates</span>
        </Col>
        <Col span={12}>
          <Button size="large" type='primary' style={{ float: "right" }}
            onClick={() => navigate("/add-email")}
          >
            <PlusOutlined />
            Add Email
          </Button>
        </Col>
      </Row>

      <Card style={{ marginTop: "10px" }}>
        <Row gutter={12}>
          <Col span={20}>
            <Input
              addonBefore="Name"
              placeholder="Search by name"
              onChange={handleInput}
              size="large"
            />
          </Col>

        </Row>

        {loader ? (
          <CulsightPageLoader />
        ) : pagination_loader ? (
          <div style={{ textAlign: "center", padding: "60px" }}>
            <Spin indicator={<LoadingOutlined spin />} size="large" />
          </div>
        ) : (
          <>
            <Row gutter={[16, 16]} style={{ marginTop: "15px" }}>
              {table_data?.map((item) => (
                <Col xs={24} sm={12} md={8} lg={8} key={item.id}>
                  <EmailTemplateCard data={item} onDelete={handleDelete} onCopy={handleCopy} onPreview={handlePreview} />
                </Col>
              ))}
            </Row>

            <div style={{ float: "right", marginTop: "20px" }}>
              <Pagination
                onChange={pagination_on_change}
                current={current_page}
                total={total_email_template}
                showSizeChanger={false}
              />
            </div>
          </>
        )}
      </Card>

      {/* 🔥 Fixed Preview Modal */}
      <Modal
        title={`Preview - ${previewSubject}`}
        open={previewModal}
        onCancel={() => setPreviewModal(false)}
        footer={null}
        width={900}
        style={{
          header: { backgroundColor: "#8c3add" }
        }}
      >
        {previewLoader ? (
          <div style={{ textAlign: "center", padding: "20px", }}>
            <Spin indicator={<LoadingOutlined spin />} size="large" />
          </div>
        ) : (
          <div
            style={{
              maxHeight: "70vh",
              overflowY: "auto",
              padding: "20px",
              borderRadius: "8px"
            }}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(previewHtml),
            }}
          />
        )}
      </Modal>
    </div>
  );
}

export default EmailList;
